/**
 * The control for the batched path: one plain `Mesh` per system with the
 * level's geometry merged into a single buffer. No `BatchedMesh`, no
 * multi-draw, no `gl_DrawID`, no per-part visibility. If this renders where
 * the batches do not, the fault is in the batching shader path, not in the
 * bundle, the lights or the session.
 */
import { BufferAttribute, BufferGeometry, Mesh, Object3D, type Material } from "three";
import { partArrays, type LevelRange, type Manifest, type PartRecord, type SystemRecord } from "./format.js";
import type { AtlasStats } from "./batched-atlas.js";

export interface MergedBuildOptions {
  level: string;
  /** Systems to build; default every system in the manifest. */
  systems?: string[];
  materialFor: (system: SystemRecord) => Material;
  /** Set each system's colour on its material (when the material has one). Default true. */
  colourSystems?: boolean;
}

export interface MergedPart {
  record: PartRecord;
  system: string;
  tris: number;
}

export class MergedAtlas {
  readonly root = new Object3D();
  readonly meshes = new Map<string, Mesh>();
  readonly parts = new Map<string, MergedPart>();

  constructor(readonly manifest: Manifest) {
    this.root.name = `${manifest.id}:merged`;
  }

  /** Per-part visibility does not exist on a merged mesh: always false. */
  setVisible(_partId: string, _visible: boolean): boolean {
    return false;
  }

  stats(): AtlasStats {
    let triangles = 0;
    for (const part of this.parts.values()) triangles += part.tris;
    // One draw per merged mesh: the floor the batched path is measured against.
    return { systems: this.meshes.size, instances: this.parts.size, triangles, culled: 0, runs: this.meshes.size };
  }
}

/** Concatenate the parts of a system into one indexed geometry. */
export function mergeParts(chunks: Map<number, ArrayBuffer>, parts: PartRecord[], level: string): BufferGeometry {
  let vertices = 0;
  let indices = 0;
  for (const part of parts) {
    const range = part.levels[level] as LevelRange;
    vertices += range.v;
    indices += range.i;
  }
  const positions = new Float32Array(vertices * 3);
  const normals = new Int16Array(vertices * 3);
  const index = new Uint32Array(indices);
  let v = 0;
  let i = 0;
  for (const part of parts) {
    const range = part.levels[level] as LevelRange;
    const buffer = chunks.get(range.chunk);
    if (!buffer) throw new Error(`chunk ${range.chunk} for ${part.id} (${level}) was not loaded`);
    const arrays = partArrays(buffer, range);
    positions.set(arrays.positions, v * 3);
    normals.set(arrays.normals, v * 3);
    for (let k = 0; k < arrays.indices.length; k++) index[i + k] = arrays.indices[k]! + v;
    v += range.v;
    i += range.i;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new BufferAttribute(normals, 3, true));
  geometry.setIndex(new BufferAttribute(index, 1));
  return geometry;
}

export function buildMergedAtlas(manifest: Manifest, chunks: Map<number, ArrayBuffer>, opts: MergedBuildOptions): MergedAtlas {
  const atlas = new MergedAtlas(manifest);
  const wanted = new Set(opts.systems ?? manifest.systems.map((s) => s.id));
  const systems = new Map(manifest.systems.map((s) => [s.id, s]));
  const bySystem = new Map<string, PartRecord[]>();
  for (const part of manifest.parts) {
    if (!wanted.has(part.system) || !part.levels[opts.level]) continue;
    let list = bySystem.get(part.system);
    if (!list) bySystem.set(part.system, (list = []));
    list.push(part);
  }
  for (const [systemId, parts] of bySystem) {
    const system = systems.get(systemId);
    if (!system) continue;
    const material = opts.materialFor(system);
    if (opts.colourSystems !== false) {
      const colour = (material as Material & { color?: { set(value: string): unknown } }).color;
      if (colour) colour.set(system.colour);
    }
    const mesh = new Mesh(mergeParts(chunks, parts, opts.level), material);
    mesh.name = `atlas:${systemId}:merged`;
    mesh.renderOrder = system.layer;
    mesh.frustumCulled = true;
    for (const part of parts) atlas.parts.set(part.id, { record: part, system: systemId, tris: (part.levels[opts.level] as LevelRange).tris });
    atlas.meshes.set(systemId, mesh);
    atlas.root.add(mesh);
  }
  return atlas;
}
